import React from 'react';
import { clsx } from 'clsx';

interface CardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  className?: string;
  variant?: 'default' | 'glow' | 'bordered';
  hover?: boolean;
}

export function Card({ 
  children, 
  title, 
  subtitle,
  className,
  variant = 'default',
  hover = false,
}: CardProps) {
  const variants = {
    default: 'glass',
    glow: 'glass glow-mixed',
    bordered: 'glass border-gradient',
  };

  return (
    <div className={clsx(
      'rounded-2xl p-6 relative overflow-hidden',
      variants[variant],
      hover && 'hover-lift cursor-pointer',
      className
    )}>
      {/* Gradient accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
      
      {/* Shimmer effect */}
      <div className="absolute inset-0 shimmer pointer-events-none" />
      
      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-8 h-8 border-l border-t border-cyan-500/20 rounded-tl-2xl" />
      <div className="absolute top-0 right-0 w-8 h-8 border-r border-t border-purple-500/20 rounded-tr-2xl" />
      
      {/* Content */}
      <div className="relative z-10">
        {(title || subtitle) && (
          <div className="mb-5">
            {title && (
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
                <h3 className="font-mono text-xs text-cyan-400 uppercase tracking-[0.2em]">
                  {title}
                </h3>
              </div>
            )}
            {subtitle && (
              <p className="text-slate-400 text-sm mt-1 ml-4">{subtitle}</p>
            )}
          </div>
        )}
        
        {children}
      </div>
    </div>
  );
}
